import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Button, Card, DatePicker, Form, Modal, Select, Spin, Table, Tag, message } from 'antd';
import moment from 'moment';
import axios from 'util/Api';
import { faviconDynamicFunction } from '../../util/methods';

const { Option } = Select;

const IotDailyProcessCheck = ({ loading, authUser, form }) => {
    const { getFieldDecorator } = form;
    const [fetching, setFetching] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [rowProcessLoading, setRowProcessLoading] = useState({});

    const onSubmit = (e) => {
        e.preventDefault();
        form.validateFields(async (err, values) => {
            if (err) {
                return;
            }

            const selectedDate = values && values.date ? values.date : null;
            const checkType = values && values.checkType ? values.checkType : 'all';

            if (!selectedDate) {
                message.error('Please select date');
                return;
            }

            const startOfDayTimestamp = moment(selectedDate).startOf('day').valueOf();
            const url = `/daily-fuel-data/iot-daily-process-check/${startOfDayTimestamp}?checkType=${encodeURIComponent(checkType)}`;

            try {
                setFetching(true);
                const response = await axios.get(url);
                const payload = response && response.data ? response.data : {};
                setReportData(Array.isArray(payload.data) ? payload.data : []);
                setSummary(payload.summary || null);
            } catch (error) {
                const errMessage = error && error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : 'Failed to fetch IoT daily process data';
                message.error(errMessage);
                setReportData([]);
                setSummary(null);
            } finally {
                setFetching(false);
            }
        });
    };

    const openDetails = (record) => {
        setSelectedRow(record || null);
        setDetailsVisible(true);
    };

    const handleProcessOrReprocess = async (record) => {
        if (!record || !record.deviceId || !record.assetId || !record.date_timestamp) {
            message.error('Invalid record. Missing device/asset/date.');
            return;
        }

        const from = moment(Number(record.date_timestamp)).startOf('day').valueOf();
        const to = moment(Number(record.date_timestamp)).endOf('day').valueOf();
        const rowKey = `${record.assetId || 'na'}-${record.deviceId || 'na'}`;

        try {
            setRowProcessLoading((prev) => ({ ...prev, [rowKey]: true }));
            const response = await axios.get(`/daily-fuel-data/process-in-period/${record.deviceId}/from/${from}/to/${to}/${record.assetId}`);
            const responseData = response && response.data ? response.data : {};
            message.success(responseData.message || 'Re-process started. You will receive notification once completed.');
        } catch (error) {
            const errMessage = error && error.response && error.response.data && error.response.data.message
                ? error.response.data.message
                : 'Failed to trigger re-process';
            message.error(errMessage);
        } finally {
            setRowProcessLoading((prev) => ({ ...prev, [rowKey]: false }));
        }
    };

    const columns = [
        {
            title: 'Asset',
            key: 'asset',
            render: (_, record) => `${record.assetCode || '--'}${record.ownerCode ? ` / ${record.ownerCode}` : ''}${record.altName ? ` / ${record.altName}` : ''}`
        },
        {
            title: 'Device ID',
            dataIndex: 'deviceId',
            key: 'deviceId'
        },
        {
            title: 'Date',
            dataIndex: 'date_timestamp',
            key: 'date_timestamp',
            render: (value) => value ? moment(Number(value)).format('DD-MM-YYYY') : '--'
        },
        {
            title: 'Has Record',
            dataIndex: 'hasRecord',
            key: 'hasRecord',
            render: (value) => value ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>
        },
        {
            title: 'Is Partial',
            dataIndex: 'is_partial_data',
            key: 'is_partial_data',
            render: (value) => value === null || value === undefined ? '--' : (value ? <Tag color="orange">Yes</Tag> : <Tag color="blue">No</Tag>)
        },
        {
            title: 'Data From',
            dataIndex: 'dataFromTimestamp',
            key: 'dataFromTimestamp',
            render: (value) => value ? moment(Number(value)).format('DD-MM-YYYY HH:mm:ss') : '--'
        },
        {
            title: 'Data To',
            dataIndex: 'dataToTimestamp',
            key: 'dataToTimestamp',
            render: (value) => value ? moment(Number(value)).format('DD-MM-YYYY HH:mm:ss') : '--'
        },
        {
            title: 'Details',
            key: 'details',
            width: 100,
            render: (_, record) => (
                <Button shape="circle" icon="info-circle" size="small" onClick={() => openDetails(record)} />
            )
        },
        {
            title: 'Action',
            key: 'action',
            width: 150,
            render: (_, record) => {
                const showAction = !record.hasRecord || record.is_partial_data === true;
                if (!showAction) {
                    return '--';
                }
                const rowKey = `${record.assetId || 'na'}-${record.deviceId || 'na'}`;
                const actionLabel = !record.hasRecord ? 'Process' : 'Re-process';
                return (
                    <Button
                        size="small"
                        type="primary"
                        loading={!!rowProcessLoading[rowKey]}
                        onClick={() => handleProcessOrReprocess(record)}
                    >
                        {actionLabel}
                    </Button>
                );
            }
        }
    ];

    faviconDynamicFunction(authUser);

    return (
        <Spin spinning={loading || fetching}>
            <Card className="gx-card-metrics" title="Iot Daily Process Check">
                <Form layout="inline" onSubmit={onSubmit}>
                    <Form.Item label="Date">
                        {getFieldDecorator('date', {
                            rules: [{ required: true, message: 'Date is required' }]
                        })(
                            <DatePicker
                                allowClear={false}
                                format="DD/MM/YYYY"
                                disabledDate={(current) => current && current > moment().endOf('day')}
                            />
                        )}
                    </Form.Item>

                    <Form.Item label="Check Type">
                        {getFieldDecorator('checkType', {
                            initialValue: 'all'
                        })(
                            <Select style={{ width: 220 }}>
                                <Option value="all">All</Option>
                                <Option value="only_processed">Only Processed</Option>
                                <Option value="only_unprocessed">Only Unprocessed</Option>
                                <Option value="only_partially_processed">Only Partially Processed</Option>
                            </Select>
                        )}
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={fetching}>
                            Submit
                        </Button>
                    </Form.Item>
                </Form>

                {summary && (
                    <div style={{ marginTop: 16 }}>
                        <Tag color="blue">Total Device Assets: {summary.totalDeviceAssets || 0}</Tag>
                        <Tag color="green">With Record: {summary.totalWithRecord || 0}</Tag>
                        <Tag color="red">Without Record: {summary.totalWithoutRecord || 0}</Tag>
                    </div>
                )}

                <div style={{ marginTop: 16 }}>
                    <Table
                        bordered
                        rowKey={(record) => `${record.assetId || 'na'}-${record.deviceId || 'na'}`}
                        dataSource={reportData}
                        columns={columns}
                        pagination={{ pageSize: 20, showSizeChanger: true }}
                        scroll={{ x: 1200 }}
                    />
                </div>
            </Card>

            <Modal
                title="Daily Process Details"
                visible={detailsVisible}
                onCancel={() => setDetailsVisible(false)}
                footer={null}
                width={700}
                destroyOnClose
            >
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                    {JSON.stringify(selectedRow && selectedRow.otherData ? selectedRow.otherData : {}, null, 2)}
                </pre>
            </Modal>
        </Spin>
    );
};

const mapStateToProps = ({ commonData, auth }) => {
    return {
        loading: commonData.loading,
        authUser: auth.authUser || commonData.authUser
    };
};

export default connect(mapStateToProps, {})(Form.create()(IotDailyProcessCheck));
